<template>
  <div
    class="game-container"
    :data-danger="dangerLevel"
    :data-distracted="isDistracted"
    role="main"
    aria-live="polite"
    @click="createRipple"
  >
    <div class="header">
      <h1 class="title" itemprop="name">{{ t('title') }}</h1>
      <select
        class="lang-select"
        v-model="currentLanguage"
        @change="setLanguage(currentLanguage)"
        aria-label="Select language"
      >
        <option value="en">English</option>
        <option value="zh-TW">中文</option>
        <option value="ja">日本語</option>
        <option value="ko">한국어</option>
        <option value="id">Bahasa Indonesia</option>
        <option value="es">Español</option>
        <option value="de">Deutsch</option>
        <option value="ar">العربية</option>
      </select>
    </div>

    <div class="message" :class="{ 'finished': isFinished }" role="status" aria-atomic="true">
      {{ message }}
    </div>
    
    <img
      src="/tungtungtungsahur.webp"
      @click="handleClick"
      class="game-image"
      :class="{ 'clickable': !isFinished }"
      :style="{ cursor: isFinished ? 'default' : 'pointer' }"
      :alt="t('title')"
      loading="eager"
      itemprop="image"
      @keydown.space.prevent="handleClick"
      role="button"
      :aria-label="t('start')"
      tabindex="0"
    />

    <div class="chase-container" role="progressbar" :aria-valuenow="progress" aria-valuemin="0" aria-valuemax="100">
      <div class="chase-progress" :style="{
        width: progress + '%',
        background: dangerLevel > 2 ? 'linear-gradient(45deg, #ff6b6b, #ff4757)' : ''
      }"></div>
      <div class="chase-character" :style="{
        left: characterPos + '%',
        transform: dangerLevel > 3 ? 'translateX(calc(var(--progress-height) / -2)) scale(1.2)' : 'translateX(calc(var(--progress-height) / -2))'
      }"></div>
    </div>

    <div v-if="isFinished" class="action-buttons">
      <button class="share-button" @click="shareResult" aria-label="分享遊戲結果">
        {{ t('share') }}
      </button>
      <button class="retry-button" @click="resetGame" aria-label="重新開始遊戲">
        {{ t('retry') }}
      </button>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { currentLanguage, t, setLanguage } from './i18n'

export default {
  name: 'App',
  setup() {
    // 確保組件卸載時清理音頻資源
    const cleanup = () => {
      [beforeClickAudio, afterClickAudio, clickAudio].forEach(audio => {
        audio.pause()
        audio.src = ''
        audio.remove()
      })
    }

    onUnmounted(() => {
      console.log('清理音頻資源')
      cleanup()
    })

    const progress = ref(0)
    const message = ref(t('start'))
    const isFinished = ref(false)
    const characterPos = ref(0)
    const dangerLevel = ref(0)
    const isDistracted = ref(false)
    const distractionTimer = ref(null)
    const distractionTimeout = ref(null)

    // 開始分心計時器
    const startDistractionTimer = () => {
      if (isFinished.value || progress.value === 0) return
      
      // 每5-15秒隨機觸發一次分心
      distractionTimer.value = setInterval(() => {
        if (!isFinished.value && !isDistracted.value) {
          isDistracted.value = true
          message.value = t('distracted')
          console.log('%c😴 Sahur is distracted!', 'color: #FF9800')
          
          // 分心持續2-4秒
          const duration = 2000 + Math.random() * 2000
          distractionTimeout.value = setTimeout(() => {
            isDistracted.value = false
            message.value = t('missed')
            console.log('%c😢 Distraction ended', 'color: #F44336')
          }, duration)
        }
      }, 5000 + Math.random() * 10000)
    }

    // 清理計時器
    const clearDistractionTimers = () => {
      if (distractionTimer.value) {
        clearInterval(distractionTimer.value)
        distractionTimer.value = null
      }
      if (distractionTimeout.value) {
        clearTimeout(distractionTimeout.value)
        distractionTimeout.value = null
      }
    }

    // 組件卸載時清理
    onUnmounted(() => {
      clearDistractionTimers()
      cleanup()
    })
    const beforeClickAudio = new Audio('/background_before_clicking.weba')
    const afterClickAudio = new Audio('/background_when_clicking.mp3')
    const clickAudio = new Audio('/click.mp3')
    
    const setupAudio = (audio, name) => {
      audio.addEventListener('loadeddata', () => {
        console.log(`${name} loaded:`, {
          duration: audio.duration,
          ready: audio.readyState
        })
      })

      audio.addEventListener('error', (e) => {
        console.error(`${name} error:`, {
          code: audio.error.code,
          message: audio.error.message,
          networkState: audio.networkState
        })
      })

      if (name.includes('before') || name.includes('after')) {
        audio.loop = true
        audio.volume = 0.3
      } else {
        audio.volume = 0.5
      }
    }

    // 設置音頻
    setupAudio(beforeClickAudio, 'before-click')
    setupAudio(afterClickAudio, 'after-click')
    setupAudio(clickAudio, 'click')

    // 音頻狀態監控
    const logAudioState = (action, audio, error = null) => {
      const state = {
        src: audio.src,
        currentTime: audio.currentTime,
        paused: audio.paused,
        ended: audio.ended,
        readyState: audio.readyState,
        networkState: audio.networkState,
        volume: audio.volume,
        muted: audio.muted,
        loop: audio.loop
      }

      const readyStates = ['HAVE_NOTHING', 'HAVE_METADATA', 'HAVE_CURRENT_DATA', 'HAVE_FUTURE_DATA', 'HAVE_ENOUGH_DATA']
      const networkStates = ['NETWORK_EMPTY', 'NETWORK_IDLE', 'NETWORK_LOADING', 'NETWORK_NO_SOURCE']
      console.group(`Audio ${action}`)
      console.log('Source:', state.src.split('/').pop())
      console.log('Ready state:', readyStates[state.readyState])
      console.log('Network state:', networkStates[state.networkState])
      console.log('Playback:', {
        time: state.currentTime.toFixed(2),
        playing: !state.paused,
        ended: state.ended,
        volume: state.volume,
        muted: state.muted,
        loop: state.loop
      })
      if (error) console.error('Error:', error)
      console.groupEnd()
    }

    // 頁面載入時播放初始背景音樂
    onMounted(() => {
      console.log('%c🎮 Game Initialized', 'font-weight: bold; font-size: 14px; color: #4CAF50;')
      beforeClickAudio.play()
        .then(() => logAudioState('Initial background music', beforeClickAudio))
        .catch(e => {
          console.warn('自動播放被阻止 - 等待用戶互動')
          logAudioState('Autoplay blocked', beforeClickAudio, e)
        })
    })

    // 監聽進度變化
    watch(progress, (newVal) => {
      characterPos.value = newVal
      // 根據進度增加危險等級
      dangerLevel.value = Math.floor(newVal / 20)
    })

    // 監聽語言變化
    watch(currentLanguage, () => {
      if (!isFinished.value) {
        message.value = t('start')
      }
    })
const handleClick = (event) => {
  if (isFinished.value) return
  createRipple(event)

  // 第一次點擊時切換背景音樂和開始分心計時器
  if (progress.value === 0) {
  console.log('%c🎵 Switching background music', 'color: #2196F3')
  
  // 確保舊音樂停止
  Promise.resolve()
    .then(() => {
      beforeClickAudio.pause()
      logAudioState('Paused before-click', beforeClickAudio)
    })
    // 開始新音樂
    .then(() => afterClickAudio.play())
    .then(() => logAudioState('Started after-click', afterClickAudio))
    .catch(e => {
      console.error('音樂切換失敗', e)
      logAudioState('Music switch failed', afterClickAudio, e)
    })
}

// 播放點擊音效
Promise.resolve()
  .then(() => {
    clickAudio.currentTime = 0
    return clickAudio.play()
  })
  .then(() => logAudioState('Click sound played', clickAudio))
  .catch(e => logAudioState('Click sound failed', clickAudio, e))

  // 無論是否分心都增加進度
  progress.value = Math.min(progress.value + 10, 100)

  // 第一次點擊時開始分心計時器
  if (progress.value === 10) {
    startDistractionTimer()
  }
  
  // 計算被抓住的機率
  // 只在非分心狀態下計算危險等級
  if (!isDistracted.value) {
    const baseChance = 0.05
    const dangerChance = dangerLevel.value * 0.05
    
    if (Math.random() < baseChance + dangerChance) {
      message.value = t('caught')
      isFinished.value = true
      afterClickAudio.pause()
      logAudioState('Game over (caught)', afterClickAudio)
      return
    }
  }

      // 更新提示訊息
      if (progress.value >= 100) {
        message.value = t('escaped')
        isFinished.value = true
        afterClickAudio.pause()
        logAudioState('Game over (escaped)', afterClickAudio)
      } else {
        // 根據危險等級顯示不同訊息
        const messages = [
          'continue',
          'careful',
          'danger',
          'veryDanger',
          'critical'
        ]
        message.value = t(messages[dangerLevel.value])
      }
    }

    const toggleLanguage = () => {
      setLanguage(currentLanguage.value === 'zh-TW' ? 'en' : 'zh-TW')
    }

    // 重置遊戲時重置音樂
    const resetGame = () => {
      console.log('Resetting game')
      progress.value = 0
      message.value = t('start')
      isFinished.value = false
      characterPos.value = 0
      dangerLevel.value = 0
      isDistracted.value = false
      clearDistractionTimers()
      
      afterClickAudio.pause()
      afterClickAudio.currentTime = 0
      logAudioState('Reset after-click', afterClickAudio)
      
      beforeClickAudio.currentTime = 0
      beforeClickAudio.play()
        .then(() => logAudioState('Reset before-click', beforeClickAudio))
        .catch(e => logAudioState('Reset before-click failed', beforeClickAudio, e))
    }


    const shareResult = async () => {
      const text = `${t(isFinished.value ? (progress.value >= 100 ? 'escaped' : 'caught') : 'start')} - ${progress.value}%`
      const shareData = {
        title: t('title'),
        text: text,
        url: window.location.href
      }

      // 建立備用的分享連結
      const fallbackShare = () => {
        const shareUrl = `whatsapp://send?text=${encodeURIComponent(shareData.text + ' - ' + shareData.url)}`
        const link = document.createElement('a')
        link.href = shareUrl
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }

      try {
        // 首先嘗試使用 Web Share API
        if (navigator.share) {
          try {
            await navigator.share(shareData)
            return
          } catch (shareError) {
            console.warn('Web Share API 失敗:', shareError)
          }
        }

        // 嘗試使用剪貼簿 API
        if (navigator.clipboard && navigator.clipboard.writeText) {
          try {
            await navigator.clipboard.writeText(`${shareData.text} - ${shareData.url}`)
            alert(currentLanguage.value === 'zh-TW' ? '結果已複製到剪貼簿！' : 'Result copied to clipboard!')
            return
          } catch (clipboardError) {
            console.warn('剪貼簿複製失敗:', clipboardError)
          }
        }

        // 使用 WhatsApp 分享作為備用方案
        fallbackShare()
      } catch (error) {
        console.error('分享失敗:', error)
        // 最後的備用方案：創建臨時的文字輸入框
        const tempInput = document.createElement('input')
        tempInput.style.position = 'absolute'
        tempInput.style.left = '-9999px'
        tempInput.value = `${shareData.text} - ${shareData.url}`
        document.body.appendChild(tempInput)
        tempInput.select()
        
        try {
          document.execCommand('copy')
          alert(currentLanguage.value === 'zh-TW' ? '結果已複製到剪貼簿！' : 'Result copied to clipboard!')
        } catch (e) {
          alert(currentLanguage.value === 'zh-TW' ? '請手動複製分享文字' : 'Please copy the share text manually')
        } finally {
          document.body.removeChild(tempInput)
        }
      }
    }

    const createRipple = (event) => {
      const ripple = document.createElement('span')
      ripple.classList.add('ripple')
      
      // 根據危險等級設置漣漪顏色
      const colors = ['#4CAF50', '#FFC107', '#FF9800', '#F44336', '#9C27B0']
      ripple.style.backgroundColor = colors[dangerLevel.value] || colors[0]
      
      const container = event.currentTarget
      const rect = container.getBoundingClientRect()
      const size = Math.max(rect.width, rect.height)
      
      ripple.style.width = ripple.style.height = `${size}px`
      ripple.style.left = `${event.clientX - rect.left - size/2}px`
      ripple.style.top = `${event.clientY - rect.top - size/2}px`
      
      container.appendChild(ripple)
      
      setTimeout(() => {
        ripple.remove()
      }, 1000)
    }

    return {
      progress,
      message,
      isFinished,
      handleClick,
      characterPos,
      dangerLevel,
      currentLanguage,
      toggleLanguage,
      shareResult,
      resetGame,
      createRipple,
      t
    }
  }
}
</script>

<style>
.header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  box-sizing: border-box;
}

.title {
  margin: 0;
  font-size: clamp(1.25rem, 3vw, 1.75rem);
  color: var(--text-primary);
  font-weight: 800;
}

.lang-select {
  padding: 0.5em 1em;
  border: 2px solid var(--surface);
  background: rgba(255, 255, 255, 0.9);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: bold;
  transition: var(--transition);
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 1em;
  padding-right: 2rem;
}

.lang-select:hover {
  background: var(--surface);
  transform: translateY(-2px);
}

.action-buttons {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
}

.share-button, .retry-button {
  padding: 0.75em 1.5em;
  font-size: 1.1em;
  border: none;
  color: white;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: bold;
  transition: var(--transition);
  box-shadow: var(--shadow-md);
}

.share-button {
  background: linear-gradient(45deg,
    hsl(210, 90%, 55%),
    hsl(280, 70%, 55%)
  );
}

.retry-button {
  background: linear-gradient(45deg,
    hsl(150, 90%, 45%),
    hsl(170, 70%, 45%)
  );
}

.share-button:hover, .retry-button:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}
.ripple {
  position: absolute;
  border-radius: 50%;
  transform: scale(0);
  animation: ripple 0.6s linear;
  pointer-events: none;
  opacity: 0.6;
}

@keyframes ripple {
  to {
    transform: scale(2);
    opacity: 0;
  }
}

.game-container {
  position: relative;
  overflow: hidden;
}

.game-image {
  position: relative;
}

.chase-container {
  position: relative;
  width: 100%;
  height: 30px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  margin: 1rem 0;
  overflow: hidden;
  transition: all 0.3s ease;
}

.chase-container[data-distracted="true"] {
  background: rgba(255, 152, 0, 0.2);
  box-shadow: 0 0 10px rgba(255, 152, 0, 0.5);
}

.chase-progress {
  position: absolute;
  height: 100%;
  background: linear-gradient(45deg, #4CAF50, #8BC34A);
  transition: width 0.3s ease;
}

.chase-progress[data-distracted="true"] {
  background: linear-gradient(45deg, #FF9800, #FFC107);
}

.chase-character {
  position: absolute;
  width: 30px;
  height: 30px;
  background: #F44336;
  border-radius: 50%;
  transition: left 0.3s ease, transform 0.3s ease;
}

.chase-character[data-distracted="true"] {
  background: #FF9800;
  transform: translateX(-15px) scale(1.1);
}
</style>